import {createStyles, Table} from '@mantine/core';
import React, {useMemo} from 'react';

import * as ComputedFieldUtil from 'schemas/fields/computed/ComputedFieldUtil';
import type {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {getTranslatedTagSchemaFromCache} from 'schemas/util/getTagSchemaFromCache';
import {columnLetter} from 'spreadsheet-import-wizard/shared/ColumnMapper';

import type {SchemaPairing} from './useMoleculeSchemasPairing';

const MAX_EXAMPLE_COLUMNS = 5;

const useStyles = createStyles((theme) => ({
  backdrop: {
    backgroundColor: theme.colors.indigo[0],
    border: `1px solid ${theme.colors.indigo[2]}`,
    borderRadius: theme.radius.sm,
    padding: theme.spacing.md,
  },
  table: {
    backgroundColor: theme.white,
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.xs,
    '& thead th': {
      backgroundColor: theme.colors.gray[1],
      fontWeight: 600,
      fontSize: theme.fontSizes.xs,
      padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
      borderBottom: `1px solid ${theme.colors.gray[3]}`,
    },
    '& tbody tr:first-of-type td': {
      fontWeight: 600,
    },
    '& tbody td': {
      padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
      fontSize: theme.fontSizes.xs,
      borderBottom: `1px solid ${theme.colors.gray[2]}`,
    },
  },
  rowNumber: {
    backgroundColor: theme.colors.gray[1],
    fontWeight: 600,
    color: theme.colors.gray[6],
    width: 30,
  },
}));

interface SpreadsheetExampleTableProps {
  schemaId: string | null;
  pairing: SchemaPairing | undefined;
}

export default function SpreadsheetExampleTable({
  schemaId,
  pairing,
}: SpreadsheetExampleTableProps): JSX.Element {
  const {classes} = useStyles();

  const exampleColumns = useMemo((): Array<string> => {
    if (!schemaId) {
      return ['Name', 'SMILES'];
    }
    const builtInNames = ['Name', 'SMILES'];

    let schemaFieldNames: Array<string> = [];
    if (pairing) {
      const formSchema = getTranslatedTagSchemaFromCache(pairing.formSchemaId);
      const compoundSchema = getTranslatedTagSchemaFromCache(pairing.compoundSchemaId);
      const allNames = new Set<string>();
      if (formSchema) {
        for (const f of ComputedFieldUtil.nonComputedFields(formSchema)) {
          if (!f.isParentLink) {
            allNames.add(f.name);
          }
        }
      }
      if (compoundSchema) {
        for (const f of ComputedFieldUtil.nonComputedFields(compoundSchema)) {
          if (!f.isParentLink) {
            allNames.add(f.name);
          }
        }
      }
      schemaFieldNames = [...allNames];
    } else {
      const schema = getTranslatedTagSchemaFromCache(schemaId);
      if (schema) {
        schemaFieldNames = ComputedFieldUtil.nonComputedFields(schema)
          .filter((f: SchemaField) => !f.isParentLink)
          .map((f: SchemaField) => f.name);
      }
    }

    return [...builtInNames, ...schemaFieldNames].slice(0, MAX_EXAMPLE_COLUMNS);
  }, [schemaId, pairing]);

  return (
    <div className={classes.backdrop}>
      <Table className={classes.table}>
        <thead>
          <tr>
            <th className={classes.rowNumber} scope="col" aria-label="Row number" />
            {exampleColumns.map((_, i) => (
              <th key={i}>{columnLetter(i)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className={classes.rowNumber}>1</td>
            {exampleColumns.map((col, i) => (
              <td key={i}>{col}</td>
            ))}
          </tr>
          <tr>
            <td className={classes.rowNumber}>2</td>
            {exampleColumns.map((col, i) => (
              <td key={i}>{i === 0 ? 'Molecule 1' : i === 1 ? 'CC(=O)O' : `value ${i + 1}`}</td>
            ))}
          </tr>
          <tr>
            <td className={classes.rowNumber}>3</td>
            {exampleColumns.map((col, i) => (
              <td key={i}>{i === 0 ? 'Molecule 2' : i === 1 ? 'c1ccccc1' : `value ${i + 1}`}</td>
            ))}
          </tr>
        </tbody>
      </Table>
    </div>
  );
}
